/**
 * Picking a picture.
 *
 * The file is downscaled in the browser before it is sent, and that is the whole
 * reason this is a component rather than an `<input type="file">` and a fetch.
 *
 * A photograph off a phone is four thousand pixels wide and several megabytes.
 * Displayed at 96, every one of those pixels is bandwidth nobody sees - paid on
 * upload, paid again in the database, and paid on every screen that shows the
 * face. Resizing on the client costs one canvas draw and is the difference
 * between a 40 KB avatar and a 4 MB one.
 *
 * It also happens to strip metadata, because a canvas carries no EXIF. That is
 * not why it is here: the server strips metadata too, and would still have to
 * if this file went straight through, because anything the browser sends is
 * whatever the sender chose to send.
 */
import { useRef, useState } from 'react';
import { ApiError, del, imageUrl, putImage } from '../api.ts';

/** Enough for a headshot at any density this UI shows one at. */
const MAX_EDGE = 512;
const JPEG_QUALITY = 0.85;

/**
 * Fit inside a square without cropping or stretching, and never scale up: a
 * 200px picture enlarged to 512 is bigger bytes and no more detail.
 */
function fit(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

/**
 * The picture, shrunk to `maxEdge` on its long side, as PNG or JPEG.
 *
 * Shared with the article's picture panel, which uses a larger edge: a
 * portrait is shown at 96px, an article picture at full column width.
 * `maxBytes`, when given, is what the server will accept - a PNG screenshot
 * that is still over it is re-encoded as JPEG rather than refused.
 */
export async function downscale(file: File, maxEdge = MAX_EDGE, maxBytes?: number): Promise<Blob> {
  // createImageBitmap decodes off the main thread, so a large photograph does
  // not freeze the page while it is being read.
  const bitmap = await createImageBitmap(file);
  try {
    const size = fit(bitmap.width, bitmap.height, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = size.width;
    canvas.height = size.height;

    const context = canvas.getContext('2d');
    if (!context) throw new Error('canvas unavailable');
    context.drawImage(bitmap, 0, 0, size.width, size.height);

    /*
     * PNG is kept as PNG so a logo or a flat graphic does not acquire JPEG
     * ringing; a photograph becomes JPEG, because a 512px photograph as PNG is
     * several times the size for no visible gain. The server accepts exactly
     * these two.
     */
    const encode = (type: 'image/png' | 'image/jpeg') =>
      new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, type, type === 'image/jpeg' ? JPEG_QUALITY : undefined),
      );
    let blob = await encode(file.type === 'image/png' ? 'image/png' : 'image/jpeg');
    if (blob && maxBytes && blob.size > maxBytes && blob.type === 'image/png') blob = await encode('image/jpeg');
    if (!blob) throw new Error('could not read that image');
    return blob;
  } finally {
    bitmap.close();
  }
}

export function ImageUpload({
  /** Current image id, or null. Only ever used to render what is there now. */
  imageId,
  /** PUT target, e.g. `/auth/me/avatar`. DELETE goes to the same path. */
  endpoint,
  label,
  /** Shown when there is no image: an initial, or a silhouette-free fallback. */
  placeholder,
  onDone,
}: {
  imageId: string | null;
  endpoint: string;
  label: string;
  placeholder: string;
  onDone: () => Promise<void> | void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(file: File) {
    setBusy(true);
    setError(null);
    try {
      await putImage(endpoint, await downscale(file));
      await onDone();
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : '画像を保存できませんでした',
      );
    } finally {
      setBusy(false);
      // So picking the same file twice in a row still fires a change event.
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await del(endpoint);
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : '画像を削除できませんでした');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="imgpick">
      <span className="imgpick__preview">
        {imageId ? (
          // Immutable URL: the browser fetches this once and caches it for a
          // year, and a replacement arrives as a different id.
          <img src={imageUrl(imageId)} alt="" width={72} height={72} />
        ) : (
          <span className="imgpick__blank" aria-hidden>
            {placeholder}
          </span>
        )}
      </span>

      <div className="imgpick__controls">
        <label className="btn btn--sm" htmlFor={`pick-${endpoint}`}>
          {busy ? '処理中…' : imageId ? '変更' : label}
        </label>
        <input
          ref={input}
          id={`pick-${endpoint}`}
          className="imgpick__input"
          type="file"
          accept="image/png,image/jpeg"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void send(file);
          }}
        />
        {imageId && (
          <button
            className="btn btn--sm btn--ghost"
            type="button"
            disabled={busy}
            onClick={() => void remove()}
          >
            削除
          </button>
        )}
        <span className="field__hint">PNG または JPEG、512KB まで。長辺512pxに縮小されます。</span>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
